import {
    BugEntity,
    BugGroupEntity,
    BugStateEntity,
    BugTransitionEntity,
    EquivBugState,
    EquivBugTransition,
    ProjectEntity,
} from "@archipad-js/archipad-model";
import { LoggerManager } from "@archipad-js/core/logger";
import { query } from "@archipad-js/core/orm";

import { isNotNullish, isSameWeek } from "@archipad-js/core/utils";

/**
 * Low level service to manage the workflow
 */
export class WorkflowService {
    /**
     * Constructor of the WorkflowService
     * Keep low level services, don't use high level managers (ex: WorkflowUserManager)
     */
    constructor(private readonly logger = LoggerManager.getLogger("WorkflowService")) {}

    findEquivBugState(equivState: EquivBugState, project: ProjectEntity): BugStateEntity | null {
        const entityContext = project.getContext();
        const bugStateQuery = query<BugStateEntity>(entityContext, "BugState");
        bugStateQuery.predicateWithFormat("equivBugState == {equivBugState}");
        const predicateParams = { equivBugState: equivState };

        const resultSet = bugStateQuery.execute(predicateParams);
        const count = resultSet.count();

        if (count != 1) {
            return null;
        }

        const bugState = resultSet.firstEntity();
        return bugState;
    }

    findEquivTransition(
        equivTransition: EquivBugTransition,
        bugState: BugStateEntity,
        project: ProjectEntity,
    ): BugTransitionEntity | null {
        const entityContext = project.getContext();
        const queryBugTransition = query<BugTransitionEntity>(entityContext, "BugTransition");
        queryBugTransition.predicateWithFormat("equivBugTransition == {equivBugTransition} AND endState == {endState}");
        const predicateParams = { equivBugTransition: equivTransition, endState: bugState };

        const resultSet = queryBugTransition.execute(predicateParams);
        const count = resultSet.count();

        // get the 1st match
        if (count == 0) {
            return null;
        }
        if (count > 1) {
            this.logger.warn("more than 1 match in find transition");
        }
        const bugTransition = resultSet.firstEntity();

        return bugTransition;
    }

    isKindOfClosed(bugState: BugStateEntity): boolean {
        return bugState.isKindOfClosed;
    }

    isKindOfFinal(bugState: BugStateEntity | null | undefined): boolean {
        if (!bugState) {
            return false;
        }
        return bugState.isKindOfFinal;
    }

    isKindOfOpen(bugState: BugStateEntity): boolean {
        return bugState.isKindOfOpen;
    }

    /**
     * Checks whether the given bug state is awaiting for approval or not.
     *
     * As of now, the only known state that means "awaiting for approval" is the
     * open state of the delivery group.
     *
     * The open state of the default group is considered as approved by default
     * and has to be explicitly rejected if not which is not the case for the
     * delivery group.
     */
    isKindOfAwaitingForApproval(bugState: BugStateEntity): boolean {
        const isKindOfOpen = this.isKindOfOpen(bugState);
        const isDeliveryGroup = bugState.bugGroup === this.bugGroupToValidateForProject(bugState.bugWorkflow.project);
        const isKindOfAwaitingForApproval = isKindOfOpen && isDeliveryGroup;
        return isKindOfAwaitingForApproval;
    }

    kindOfOpenArray(): EquivBugState[] {
        return [EquivBugState.EquivStateOpen, EquivBugState.EquivStateOpenAlt];
    }

    isFinaleStateOrRelatedToFinaleState(bug: BugEntity): boolean {
        if (!bug) {
            return false;
        }
        const isKindOfFinal = this.isKindOfFinal(bug.stateNoteCache?.bugState);
        const isRelatedToFinal = [...bug.relatedChildBugs, bug.relatedParentBug]
            .filter(isNotNullish)
            .some((rBug) => this.isKindOfFinal(rBug.stateNoteCache?.bugState));
        return isKindOfFinal || isRelatedToFinal;
    }

    bugsToValidateForProject(project: ProjectEntity): BugEntity[] {
        const bugGroup = this.bugGroupToValidateForProject(project);
        const entityContext = project.getContext();
        const q = query<BugEntity>(entityContext, "Bug");
        q.predicateWithFormat(
            "stateNoteCache.bugState.equivBugState == {equivStateOpen} OR stateNoteCache.bugState.equivBugState == {equivStateOpenAlt}",
            { equivStateOpen: EquivBugState.EquivStateOpen, equivStateOpenAlt: EquivBugState.EquivStateOpenAlt },
        );
        q.predicateWithFormat("bugGroup == {bugGroup}", { bugGroup: bugGroup });
        q.orderBy("index");
        const bugs = q.execute();
        return bugs.sortedEntities();
    }

    bugGroupToValidateForProject(project: ProjectEntity): BugGroupEntity | null {
        if (!project.isBugWorkflowEnabled) {
            return null;
        }
        if (!project.bugWorkflow?.bugGroups || project.bugWorkflow.bugGroups.length == 0) {
            return null;
        }
        const entityContext = project.getContext();
        const q = query<BugGroupEntity>(entityContext, "BugGroup");
        q.orderBy("name asc");
        return q.execute().firstEntity();
    }

    /**
     * Returns a sorted list of {@link BugStateEntity}.
     *
     * Use {@link BugStateEntity.orderHint} to sort states. If every states of
     * the given {@link BugGroupEntity} do NOT have orderHint, it fallbacks on
     * an automatic traversal algorithm on {@link BugTransitionEntity}.
     *
     * @param bugGroup The {@link BugGroupEntity} to filter states
     */
    listStates(project: ProjectEntity, bugGroup: BugGroupEntity | null | undefined): readonly BugStateEntity[] {
        const entityContext = project.getContext();

        const q = query<BugStateEntity>(entityContext, "BugState");
        if (bugGroup !== undefined) {
            q.predicateWithFormat("bugGroup == {bugGroup}", { bugGroup });
        }
        q.orderBy("orderHint asc, id asc");

        const resultSet = q.execute();
        const states = resultSet.sortedEntities();

        const hasOrderHint = states.every((state) => state.orderHint !== 0);
        if (!hasOrderHint) {
            return this.listStatesAuto(project, bugGroup);
        }

        return states;
    }

    /**
     * List all {@link BugStateEntity} of given {@link BugGroupEntity} using an
     * automatic traversal algorithm on {@link BugTransitionEntity}.
     *
     * It walks through all enter transitions (i.e: null => open) and list all
     * transitions leading to those states (i.e: open => solved), then again
     * list all transitions leading to those states (i.e: solved => closed)
     * and so on until all states have been walked.
     */
    private listStatesAuto(
        project: ProjectEntity,
        bugGroup: BugGroupEntity | null | undefined,
    ): readonly BugStateEntity[] {
        const entityContext = project.getContext();

        const q = query<BugTransitionEntity>(entityContext, "BugTransition");
        if (bugGroup !== undefined) {
            q.predicateWithFormat("endState.bugGroup == {bugGroup}", { bugGroup });
        }
        q.predicateWithFormat("equivBugTransition == {equivTransitionEnter}", {
            bugGroup,
            equivTransitionEnter: EquivBugTransition.EquivTransitionEnter,
        });
        q.orderBy("id");

        const resultSet = q.execute();
        const enterTransitions = resultSet.sortedEntities();

        const sortedStates: BugStateEntity[] = [];

        for (const enterTransition of enterTransitions) {
            this.listStatesTraversal(sortedStates, enterTransition);
        }

        return sortedStates;
    }

    /**
     * Traverse the given {@link BugTransitionEntity transition} to find all
     * related states.
     *
     * @see {@link listStatesAuto}
     *
     * @param sortedStates List of sorted {@link BugStateEntity states} already listed, used to avoid duplication and infinite recursion
     * @param enterTransition The enter {@link BugTransitionEntity transition} to walk through
     */
    private listStatesTraversal(sortedStates: BugStateEntity[], enterTransition: BugTransitionEntity): void {
        const state = enterTransition.endState;

        sortedStates.push(state);

        /**
         * Sort transition by {@link BugTransition.equivBugTransition}
         *
         * 1. Enter transition
         * 2. Reject transition
         * 3. Accept transition
         * 4. None transition
         */
        const transitions = state.bugTransitionStarts.slice().sort((transitionA, transitionB) => {
            const diff = transitionB.equivBugTransition - transitionA.equivBugTransition;

            if (diff !== 0) {
                // can sort by transition
                return diff;
            }

            const isCloseStateA = this.isKindOfClosed(transitionA.endState);
            const isCloseStateB = this.isKindOfClosed(transitionB.endState);

            if (isCloseStateA === isCloseStateB) {
                // both transitions end in a final state, do nothing
                return 0;
            }

            if (isCloseStateA) {
                // transition A ends in final state, not B: sort A after B
                return 1;
            }

            // transition B ends in final state, not A: sort A before B
            return -1;
        });

        for (const transition of transitions) {
            if (sortedStates.includes(transition.endState)) {
                continue;
            }

            this.listStatesTraversal(sortedStates, transition);
        }
    }

    isDeliveryGroup(bugGroup: BugGroupEntity): boolean {
        return bugGroup?.templateName === "deliveryGroup";
    }

    findLastBugState(project: ProjectEntity, bug: BugEntity, before: Date): BugStateEntity | null {
        const isActualWeekRange = isSameWeek(before, new Date());
        if (isActualWeekRange) {
            return bug.stateNoteCache?.bugState ?? null;
        }
        const lastStateNoteInRange = bug.notes
            .filter((note) => note.bugState != null)
            .filter((note) => {
                return note.date <= before;
            })
            .sort((a, b) => a.creationDate.getTime() - b.creationDate.getTime())
            .pop();

        if (lastStateNoteInRange) {
            return lastStateNoteInRange.bugState;
        }

        // no state note in range for bug
        // Convert on the fly
        const isClosed = bug.closeDate && bug.closeDate <= before;
        if (isClosed) {
            return this.findEquivBugState(EquivBugState.EquivStateClosed, project);
        }
        return this.findEquivBugState(EquivBugState.EquivStateOpen, project);
    }
}
